import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BarChart3, RefreshCw } from 'lucide-react';
import { AnalyticsResponse } from './types';
import SeasonRecordChart from './SeasonRecordChart';
import MatchBreakdownChart from './MatchBreakdownChart';
import SeasonDonut from './SeasonDonut';
import PlayerLeaderboard from './PlayerLeaderboard';
import PartnershipHeatmap from './PartnershipHeatmap';
import StrengthOfScheduleChart from './StrengthOfScheduleChart';
import FlightExplorer from './FlightExplorer';
import ChartPanel from './ChartPanel';

interface Props {
  teamId: number;
}

const formatSyncedAt = (iso: string | null): string => {
  if (!iso) return 'never';
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const AnalyticsTab: React.FC<Props> = ({ teamId }) => {
  const [data, setData] = useState<AnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    axios
      .get(`/teams/${teamId}/analytics`)
      .then((res) => {
        if (cancelled) return;
        setData(res.data as AnalyticsResponse);
      })
      .catch((err) => {
        if (cancelled) return;
        const status = err?.response?.status;
        if (status === 403) {
          setError('Analytics is captain-only.');
        } else {
          setError('Unable to load analytics. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [teamId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="analytics-loading">
        <RefreshCw className="w-5 h-5 animate-spin text-gray-400" />
        <span className="ml-2 text-sm text-gray-500">Loading analytics…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-12 text-center" data-testid="analytics-error">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const isEmpty = data.actual_match_count === 0;
  const strengthOfSchedule = data.strength_of_schedule;
  const hasStrengthOfSchedule = !!(strengthOfSchedule && strengthOfSchedule.length > 0);

  return (
    <div className="space-y-4" data-testid="analytics-tab">
      {isEmpty ? (
        <div className="bg-white dark:bg-gray-800 shadow-sm rounded-xl border border-gray-100 dark:border-gray-700/50 p-8 text-center" data-testid="analytics-empty">
          <BarChart3 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-1">
            No completed match results yet
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 max-w-md mx-auto">
            Sync from TennisRecord on the Lineups tab to pull in past match results and unlock
            analytics.
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-primary-100 dark:bg-primary-900/30 rounded-lg">
                <BarChart3 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Analytics</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {data.actual_match_count} match{data.actual_match_count === 1 ? '' : 'es'} with
                  results · Last synced {formatSyncedAt(data.last_synced)}
                </p>
              </div>
            </div>
          </div>

          {/*
            Structured grid (not CSS masonry): charts sit in aligned 2-up rows that
            match heights, while the inherently wide blocks (leaderboard, heatmap)
            span the full width so nothing looks staggered or cramped.
          */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="analytics-grid">
            {/* Leaderboard leads — the headline view, promoted above the charts */}
            <ChartPanel title="Player Leaderboard" subtitle="Click a column to sort" span="full">
              <PlayerLeaderboard rows={data.player_leaderboard} />
            </ChartPanel>

            {/* Row 1 — season overview */}
            <ChartPanel title="Season Record" subtitle="Cumulative wins and losses over time">
              <SeasonRecordChart data={data.season_record} />
            </ChartPanel>
            <ChartPanel
              title="Season Win Rate"
              subtitle="Won vs lost across the season — matches, lines, sets, games"
            >
              <SeasonDonut data={data.season_totals} />
            </ChartPanel>

            {/* Row 2 — per-match breakdown (full width when there's no schedule chart to pair with) */}
            <ChartPanel
              title="Match Breakdown"
              subtitle="Won vs lost per match — toggle units"
              span={hasStrengthOfSchedule ? undefined : 'full'}
            >
              <MatchBreakdownChart data={data.match_scorelines} />
            </ChartPanel>
            {hasStrengthOfSchedule && strengthOfSchedule && (
              <ChartPanel
                title="Strength of Schedule"
                subtitle="Opponent − team average dynamic rating per match"
              >
                <StrengthOfScheduleChart data={strengthOfSchedule} />
              </ChartPanel>
            )}

            {/* Full-width data block */}
            <ChartPanel
              title="Partnership Chemistry"
              subtitle="Win % per pairing — diagonal cells show singles"
              span="full"
            >
              <PartnershipHeatmap matrix={data.partnership_chemistry} />
            </ChartPanel>
          </div>
        </>
      )}

      {/* Flight scout board — captain-only by virtue of living in this captain-gated tab. */}
      <FlightExplorer teamId={teamId} />
    </div>
  );
};

export default AnalyticsTab;
